import { escapeFileViewerHtml } from './file-viewer-shell';
import {
  renderPackageFileSnapshotUnavailableNotice,
  renderPackageFileSnapshotViewer,
  type PackageFileViewerFile,
} from './package-file-viewer';

export type PackageReviewFile = PackageFileViewerFile;
export type PackageReviewSurfaceKind = 'published-skill' | 'submission-review';

export type PackageReviewFileInput = {
  path: string;
  content?: string | null;
  status?: string | null;
  content_size?: number | null;
  old_path?: string | null;
};

type PackageReviewSurfaceCopy = {
  sectionTitle: string;
  sectionIconClass: string;
  viewerTitle: string;
  description: (count: number) => string;
  emptyNotice: string;
  emptyTitle: string;
  emptyDescription: string;
};

export type PackageReviewFileViewerOptions<TFile extends PackageReviewFile = PackageReviewFile> = {
  surface: PackageReviewSurfaceKind;
  files: TFile[];
  currentIndex: number | null;
  selectFile: string;
  copyFileContent: string;
  title?: string;
  description?: string;
  emptyTitle?: string;
  emptyDescription?: string;
};

export type PackageReviewFilesSectionOptions<TFile extends PackageReviewFile = PackageReviewFile> =
  PackageReviewFileViewerOptions<TFile> & {
    containerId: string;
    headingLevel?: 2 | 3;
    sectionTitle?: string;
    sectionIconClass?: string;
    unavailableMessage?: string;
  };

function fileCountLabel(count: number): string {
  return count + ' file' + (count === 1 ? '' : 's');
}

function surfaceCopy(surface: PackageReviewSurfaceKind): PackageReviewSurfaceCopy {
  if (surface === 'submission-review') {
    return {
      sectionTitle: 'Review Packet Files',
      sectionIconClass: 'fas fa-file-code text-blue-400',
      viewerTitle: 'Submitted package files',
      description: (count) => fileCountLabel(count) + ' captured from this submission.',
      emptyNotice: 'No files are available in this review packet.',
      emptyTitle: 'No submitted package files',
      emptyDescription: 'Push code before reviewing package contents.',
    };
  }

  return {
    sectionTitle: 'Package Files',
    sectionIconClass: 'fas fa-box-open text-brand-400',
    viewerTitle: 'Package files',
    description: (count) => fileCountLabel(count) + ' in this published skill.',
    emptyNotice: 'No stored package snapshot files found for this skill. Please republish from an ingested repository commit.',
    emptyTitle: 'No package files',
    emptyDescription: 'Republish from an ingested repository commit to store a file snapshot.',
  };
}

export function normalizePackageReviewFiles<TFile extends PackageReviewFileInput>(
  files: readonly TFile[],
): PackageReviewFile[] {
  return files.map((file) => {
    const content = file.content ?? '';
    return {
      path: file.path,
      content,
      status: file.status ?? null,
      content_size: file.content_size ?? (typeof content === 'string' ? content.length : null),
      old_path: file.old_path ?? null,
    };
  });
}

export function renderPackageReviewFileViewer<TFile extends PackageReviewFile>(
  options: PackageReviewFileViewerOptions<TFile>,
): string {
  const copy = surfaceCopy(options.surface);
  return renderPackageFileSnapshotViewer({
    files: options.files,
    currentIndex: options.currentIndex,
    selectFile: options.selectFile,
    copyFileContent: options.copyFileContent,
    title: options.title || copy.viewerTitle,
    description: options.description || copy.description(options.files.length),
    emptyTitle: options.emptyTitle || copy.emptyTitle,
    emptyDescription: options.emptyDescription || copy.emptyDescription,
  });
}

export function renderPackageReviewFilesSection<TFile extends PackageReviewFile>(
  options: PackageReviewFilesSectionOptions<TFile>,
): string {
  const copy = surfaceCopy(options.surface);
  const headingTag = options.headingLevel === 3 ? 'h3' : 'h2';
  const sectionTitle = options.sectionTitle || copy.sectionTitle;
  const iconClass = options.sectionIconClass || copy.sectionIconClass;

  return '<section class="card p-4">' +
    '<' + headingTag + ' class="text-sm font-semibold text-zinc-300 mb-3">' +
      '<i class="' + escapeFileViewerHtml(iconClass) + ' mr-2"></i>' +
      escapeFileViewerHtml(sectionTitle) +
    '</' + headingTag + '>' +
    (options.files.length
      ? '<div id="' + escapeFileViewerHtml(options.containerId) + '">' +
          renderPackageReviewFileViewer(options) +
        '</div>'
      : renderPackageFileSnapshotUnavailableNotice(options.unavailableMessage || copy.emptyNotice)) +
  '</section>';
}
